import React, { useEffect, useState } from 'react';
import { Form, Input, Radio, Select, Spin, Button, message } from 'antd';
import axios from 'axios';
import CustomerMainLayout from '../CustomerMainLayout';
import ENVIRONMENT from '../../../../environments';
import NotificationControllerForm from '../../AdminComponents/NotificationController/NotificationControllerForm';
import DottedLoader from '../../DottedLoader/DottedLoader';

const { Option } = Select;

const AdminCreateUser = () => {
  const [form] = Form.useForm();
  const [companyOption, setCompanyOption] = useState('addExisting');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [emailExistMessage, setEmailExistMessage] = useState('');
  const [allUsersLoading, setAllUsersLoading] = useState(true);
  const [companyExists, setCompanyExists] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    product: true,
    space: true,
    material: true,
    scene: true
  });

  const handleRadioChange = e => {
    setCompanyOption(e.target.value);
  };

  const handleNotificationChange = (e, type) => {
    setNotificationSettings(prev => ({
      ...prev,
      [type]: e.target.checked
    }));
  };

  const handleEmailFocus = (e) => {
    setCompanyExists(false);
    setEmailExistMessage('');
  }

  const handleEmailBlur = (e) => {
    const emailValue = e.target.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailValue && !emailRegex.test(emailValue)) {
      console.log('Email is not valid');
    } else {
      // Extract domain from email
      const domain = emailValue.split('@')[1];
      console.log('Domain:', domain);
      
      // Find users with the same domain
      const usersWithSameDomain = allUsers.filter(user => {
        const userDomain = user.email.split('@')[1];
        return userDomain === domain;
      });
      
      if (usersWithSameDomain.length > 0) {
         let singleUser = usersWithSameDomain[0];
         const domain = singleUser.email.split('@')[1];
          const companyName = singleUser.company_display_name;
          setEmailExistMessage(
            <div
              style={{
                backgroundColor: '#f5f5f5',
                borderLeft: '4px solid #1890ff',
                padding: '10px 12px',
                borderRadius: '4px',
                color: '#333',
                fontSize: '13px',
                marginTop: '8px',
                lineHeight: '1.5'
              }}
            >
              A user with the email domain <strong>{domain}</strong> already exists in company{' '}
              {companyName ? <strong>({companyName})</strong> : ''}.<br />
              Please select the appropriate company from the list below.
            </div>
          );

         // Check if singleUser's company_id exists in companies list
         const companyExists = companies.some(company => company.company_id === singleUser.company_id);
         if(!companyExists) {
           let newCompany = {
            company_id: singleUser.company_id,
            company_name: singleUser.company_display_name,
           }
           setCompanies(prev => {
             const updatedCompanies = [...prev, newCompany];
             // Sort companies alphabetically by company_name
             return updatedCompanies.sort((a, b) => 
               a.company_name.localeCompare(b.company_name)
             );
           });
           setCompanyExists(true);
         }
      } 
    }
  };


  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const payload = { designer_username: localStorage.getItem('username') };
        const response = await axios.post(ENVIRONMENT.GET_MANAGED_SERVICE_RELATION, payload);
        const data = response.data;

        const uniqueCompaniesMap = new Map();
        data.forEach(entry => {
          if (!uniqueCompaniesMap.has(entry.company_id)) {
            uniqueCompaniesMap.set(entry.company_id, entry.company_name);
          }
        });

        const uniqueCompanies = Array.from(uniqueCompaniesMap, ([id, name]) => ({
          company_id: id,
          company_name: name,
        }));

        // Sort companies alphabetically by company_name
        const sortedCompanies = uniqueCompanies.sort((a, b) => 
          a.company_name.localeCompare(b.company_name)
        );

        setCompanies(sortedCompanies);
      } catch (error) {
        console.error('Error fetching company data:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchAllUsers = async () => {
      setAllUsersLoading(true);
      const response = await axios.post(ENVIRONMENT.CUSTOMER_GET_BATCH, {});
      setAllUsers(response.data);
      setAllUsersLoading(false);
    }

    fetchAllUsers();
    fetchCompanies();
  }, []);
  
  const handleFormSubmit = async values => {
    setSubmitLoading(true);
    // Clear any existing errors
    setUsernameError('');
    setEmailError('');
    setEmailExistMessage('');

    const {
      firstName,
      lastName,
      email,
      username,
      password,
      companyId,
      companyName
    } = values;

    const selectedCompanyName =
      companyOption === 'addExisting'
        ? companies.find(c => c.company_id === companyId)?.company_name || ''
        : companyName;

    const profileName =
      (firstName || '') + (lastName ? ` ${lastName}` : '') || username;

    const payload = {
      first_name: firstName || '',
      last_name: lastName || '',
      email,
      company_name: selectedCompanyName,
      username,
      password,
      password_confirm: password,
      profile_name: profileName.trim() || username,
      user_group: 'customer',
      created_by: localStorage.getItem('username'),
      notification_settings: notificationSettings
    };
    if (companyOption == 'addExisting') {
        payload.create_new_company = false;
        payload.company_id = companyId;
    } else {
        payload.create_new_company = true;
    }

    console.log(payload)

    try {
      const response = await axios.post(ENVIRONMENT.ADMIN_CREATE_USER, payload);
      if (response.data.email_exists && response.data.username_exists) {
        setUsernameError('User already exists');
        return;
      } else if (response.data.email_exists) {
        setEmailError('Email already exists');
        return;
      } else if (response.data.username_exists) {
        setUsernameError('Username already exists');
        return;
      }
      message.success('User created successfully');
      console.log('Create User Response:', response.data);
      form.resetFields();
      setNotificationSettings({
        product: true,
        space: true,
        material: true,
        scene: true
      });
    } catch (error) {
      console.error('Error creating user:', error);
      message.error('Failed to create user');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <CustomerMainLayout selected="3">
      <div style={{ width: 500, marginTop: 36 }}>
        {allUsersLoading ? (
          <DottedLoader />
        ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            companyOption: 'addExisting',
          }}
          onFinish={handleFormSubmit}
        >
          <Form.Item label="First Name" name="firstName">
            <Input placeholder="Enter first name" />
          </Form.Item>

          <Form.Item label="Last Name" name="lastName">
            <Input placeholder="Enter last name" />
          </Form.Item>

          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <div>
              <Input 
                placeholder="Enter email address" 
                onChange={() => {
                  if (emailError) setEmailError('');
                }}
                onBlur={handleEmailBlur}
                onFocus={handleEmailFocus}
              />
              {emailError && (
                <span style={{ color: '#ff4d4f', display: 'block', marginTop: '4px' }}>
                  {emailError}
                </span>
              )}
            </div>
          </Form.Item>

          <Form.Item
            label="Username"
            name="username"
            rules={[
              { required: true, message: 'Please input your username!' }
            ]}
          >
            <div>
              <Input 
                placeholder="Enter username" 
                autoComplete="off"
                onChange={() => {
                  if (usernameError) setUsernameError('');
                }}
              />
              {usernameError && (
                <span style={{ color: '#ff4d4f', display: 'block', marginTop: '4px' }}>
                  {usernameError}
                </span>
              )}
            </div>
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              {
                required: true,
                message: <span className='manrope f-14 red'>Please enter a password</span>,
              },
              {
                pattern:
                  "^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$",
                message: <span className='manrope f-14 red'>Password must be 8+ characters with at least 1 uppercase, 1 lowercase, 1 digit, and 1 special character</span>,
              },
            ]}
            autoComplete="new-password"
          >
            <Input.Password placeholder="Enter password" autoComplete="new-password" />
          </Form.Item>

          <Form.Item name="companyOption" label="Company Options">
            <Radio.Group onChange={handleRadioChange}>
              <Radio value="addExisting">Add to Existing Company</Radio>
              <Radio value="newCompany">New Company User</Radio>
            </Radio.Group>
          </Form.Item>

          {companyOption === 'addExisting' ? (
            <Form.Item
              label="Company List"
              name="companyId"
              rules={[{ required: true, message: 'Please select a company!' }]}
            >
              {loading ? (
                <Spin />
              ) : (
                <Select placeholder="Select a company">
                  {companies.map(company => (
                    <Option key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          ) : (
            <Form.Item
              label="Company Name"
              name="companyName"
              rules={[{ required: true, message: 'Please enter the company name!' }]}
            >
              <Input placeholder="Enter new company name" />
            </Form.Item>
          )}

          {emailExistMessage && (
            <div className="info-message">
              {emailExistMessage}
            </div>
          )}

          {/* Notification Settings */}
          <Form.Item>
            <NotificationControllerForm
              notification_settings={notificationSettings}
              setNotificationSettings={handleNotificationChange}
            />
          </Form.Item>

          {/* Submit Button */}
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitLoading}>
              Create User
            </Button>
          </Form.Item>
          </Form>
        )}
      </div>
    </CustomerMainLayout>
  );
};

export default AdminCreateUser;
